/**
 * Gross profit (GP) field detection and stripping.
 *
 * Used to enforce the `orders:gp:view` RBAC action across the application.
 * Anywhere GP-derived values could leak — list responses, report aggregates,
 * AI tool outputs, line items — we strip the keys before they leave the
 * backend, and we render a lock icon if any GP value somehow reaches the UI.
 */

/**
 * Canonical RBAC action that gates gross-profit visibility everywhere.
 * Hard-coded here to avoid a cycle with `RbacActions` (this util is imported
 * by code that builds those structures).
 */
export const GP_VIEW_PERMISSION = 'orders:gp:view';

/**
 * Pattern that detects gross-profit-related field names. Three classes:
 *
 *   1. Substring match: "grossprofit" / "gross_profit" / "grossmargin" /
 *      "gross_margin" anywhere in the key (case-insensitive). Catches
 *      grossProfit, GROSS_PROFIT, previousGrossMargin, etc.
 *
 *   2. "GP/Gp/gp" as a camelCase / snake_case token. Catches:
 *        totalGP / totalGp / TOTAL_GP
 *        gpMargin / gp_margin / gpPercent / gpAmount
 *        totalGPAggregate / rollingTotalGpAggregate30
 *        rollingTotalGpMarginTrend90 / totalGPRank / totalGPReorderRank
 *      The "gp" token must be preceded by start, end, underscore, or a
 *      letter forming a camelCase boundary, AND followed by an uppercase
 *      letter, digit, underscore, or end. Excludes substrings like
 *      `gpu`, `gprs`, `gpio` where "gp" isn't the gross-profit token.
 *
 *   3. "margin" as a token. In this codebase every "margin" field is
 *      GP-derived (totalMargin = SUM(invoice.totalGP); marginContribution
 *      = customer's avg margin; gpMargin; marginTrend). Catches:
 *        totalMargin / total_margin / prev_margin
 *        marginContribution / avgMargin / marginTrend
 *      Bias is intentional: false positives strip a non-GP "margin"
 *      field (annoying), false negatives leak GP (security issue).
 */
const GROSS_PROFIT_SUBSTRING =
	/(?:grossprofit|gross_profit|grossmargin|gross_margin)/i;
const GP_TOKEN_REGEX = /(?:^|[a-z]|[^a-zA-Z])(?:GP|Gp|gp)(?:[A-Z_0-9]|$)/;
const MARGIN_TOKEN_REGEX =
	/(?:^|[a-z]|[^a-zA-Z])(?:Margin|margin)(?:[A-Z_0-9]|$)/;
/**
 * "profit" / "profitability" anywhere in the key. Profitability is by
 * definition derived from gross profit, so the customer scorecard's
 * `profitability` branch (score, previousScore, marginContribution,
 * discountUsage, sparkline) and any *profit* / profitabilityScore /
 * previousProfit fields fall under the same RBAC.
 *
 * Substring (not token) is intentional so "profitability" itself
 * matches even though "profit" is followed by lowercase letters.
 */
const PROFIT_SUBSTRING = /profit/i;

/**
 * Returns true when the given key represents a gross-profit value.
 *
 * Recognizes:
 *   - direct "GP" / "Gp" / "gp" tokens at camelCase boundaries
 *     (totalGP, totalGp, gpMargin, gp_margin)
 *   - prefixed + suffixed variants (rollingTotalGpAggregate30,
 *     rollingTotalGpMarginTrend90)
 *   - any field containing "grossprofit" / "gross_profit"
 *     (grossProfit, GROSS_PROFIT, totalGrossProfit)
 *
 * Conservative bias toward over-matching: a false positive strips a
 * non-GP field (annoying), a false negative leaks GP (security issue).
 */
export function isGpFieldName(key: string): boolean {
	if (key == null || key === '') {
		return false;
	}
	if (GROSS_PROFIT_SUBSTRING.test(key)) {
		return true;
	}
	if (PROFIT_SUBSTRING.test(key)) {
		return true;
	}
	if (GP_TOKEN_REGEX.test(key)) {
		return true;
	}
	return MARGIN_TOKEN_REGEX.test(key);
}

/**
 * Recursively walk `value` and delete any property whose key is a GP field.
 *
 * - Mutates objects in place for efficiency (response payloads are throw-away).
 * - Recurses into nested objects and arrays.
 * - Does NOT descend into class instances with their own prototype methods
 *   (Date, Buffer, Map, Set, etc.) — those don't carry GP keys.
 * - Tracks visited references to short-circuit on cycles.
 */
export function stripGpFields<T>(value: T): T {
	return stripFieldsMatching(value, isGpFieldName);
}

/**
 * Generic recursive field stripper: deletes every property whose key the
 * matcher flags, using the same walk (in-place mutation, cycle tracking,
 * built-in skipping) documented on `stripGpFields`. Shared by the GP and
 * net-cost sanitizers so every RBAC-stripped field family behaves
 * identically.
 */
export function stripFieldsMatching<T>(
	value: T,
	matcher: (key: string) => boolean,
): T {
	const visited = new WeakSet<object>();
	stripInPlace(value, visited, matcher);
	return value;
}

function stripInPlace(
	value: unknown,
	visited: WeakSet<object>,
	matcher: (key: string) => boolean,
): void {
	if (value == null || typeof value !== 'object') {
		return;
	}
	if (visited.has(value as object)) {
		return;
	}
	visited.add(value as object);

	if (Array.isArray(value)) {
		for (const item of value) {
			stripInPlace(item, visited, matcher);
		}
		return;
	}

	if (!isPlainObjectLike(value)) {
		return;
	}

	const obj = value as Record<string, unknown>;
	for (const key of Object.keys(obj)) {
		if (matcher(key)) {
			delete obj[key];
			continue;
		}
		stripInPlace(obj[key], visited, matcher);
	}
}

/**
 * Plain-object check: descend into POJOs and TypeORM entities (whose
 * prototype is Object.prototype or the entity class), but skip built-ins
 * like Date, Buffer, Map, Set, etc. that we don't need to walk.
 */
function isPlainObjectLike(value: object): boolean {
	if (value instanceof Date) return false;
	if (value instanceof Map) return false;
	if (value instanceof Set) return false;
	if (value instanceof RegExp) return false;
	if (typeof Buffer !== 'undefined' && value instanceof Buffer) return false;
	return true;
}
